import { ChangeEvent, useEffect } from 'react';
import { findOption } from '../utils';
import { AutocompleteProps } from '../types';
import { DropDownOption } from 'src/types';

type UseOuterValueCheckParams = Pick<
  AutocompleteProps,
  'value' | 'onChange' | 'defaultValue' | 'options'
> & {
  isFocused: boolean;
  makeChangeEvent: (value: string) => ChangeEvent<HTMLInputElement>;
  setRefValue: (value: string) => void;
  filterOptions: (value: string) => DropDownOption[];
  handleSelectOption: (option: DropDownOption) => void;
};

/** Содержит внутри проверочные эффекты, которые следят за value и defaultValue на
 * случай, если через них производится попытка установить несуществующее в опциях
 * значение
 */
export const useOuterValueCheck = ({
  isFocused,
  value,
  onChange,
  makeChangeEvent,
  setRefValue,
  defaultValue,
  options = [],
  filterOptions,
  handleSelectOption
}: UseOuterValueCheckParams) => {
  useEffect(() => {
    /** Этот эффект проверяет попытку установки value извне */
    if (!isFocused && value && value.length > 0) {
      const suggestions = filterOptions(value);
      // Если соответствующих опций не найдено - очищаем инпут
      if (suggestions.length === 0) {
        onChange?.(makeChangeEvent(''));
        setRefValue('');
      }
      // Если найдено точное совпадение - обновляем внутренний реф
      // и подобранные подсказки под новое значение
      if (suggestions.length === 1) {
        handleSelectOption(suggestions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    /** Этот эффект не допускает подстановки несуществующего значения извне через
     * defaultValue либо если такое существует, то обновляет реф в новое значение */
    if (!defaultValue) return;
    const possibleOption = findOption(defaultValue, options);
    if (possibleOption) {
      onChange?.(makeChangeEvent(possibleOption.name), possibleOption);
    } else {
      onChange?.(makeChangeEvent(''));
      setRefValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);
};
